import { useRef, useState } from 'react';
import { haptic } from '@/lib/telegram';
import { api } from '@/api/client';
import type { UploadedPhoto } from '@/types/domain';
import styles from './PhotoPicker.module.css';

interface Props {
  photos: UploadedPhoto[];
  onChange: (photos: UploadedPhoto[]) => void;
  max?: number;
}

// Локальное состояние загрузки: превью + временный id
interface Pending {
  tempId: string;
  previewUrl: string;
}

export const PhotoPicker = ({ photos, onChange, max = 8 }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<Pending[]>([]);

  // photos может измениться между await при параллельной загрузке —
  // держим свежую ссылку, чтобы загрузки не затирали друг друга.
  const photosRef = useRef(photos);
  photosRef.current = photos;

  const handleSelect = async (files: FileList | null) => {
    if (!files) return;
    haptic.light();

    const remaining = max - photos.length - pending.length;
    const toAdd = Array.from(files).slice(0, remaining);

    for (const file of toAdd) {
      const tempId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const previewUrl = URL.createObjectURL(file);

      setPending((p) => [...p, { tempId, previewUrl }]);

      try {
        const uploaded = await api.uploadPhoto(file);

        onChange([...photosRef.current, uploaded]);
        setPending((p) => p.filter((x) => x.tempId !== tempId));
        URL.revokeObjectURL(previewUrl);
      } catch (err) {
        console.error('[PhotoPicker] upload failed:', err);
        setPending((p) => p.filter((x) => x.tempId !== tempId));
        URL.revokeObjectURL(previewUrl);
        alert('Не удалось загрузить фото. Попробуй ещё раз.');
      }
    }
  };

  const removeAt = (idx: number) => {
    haptic.light();
    onChange(photos.filter((_, i) => i !== idx));
  };

  const totalCount = photos.length + pending.length;

  return (
    <div className={styles.grid}>
      {/* Загруженные фото */}
      {photos.map((photo, i) => (
        <div key={photo.storage_key} className={styles.tile}>
          <img src={photo.url_thumb} alt="" className={styles.image} />
          {i === 0 && <span className={styles.coverBadge}>обложка</span>}
          <button
            type="button"
            className={styles.removeBtn}
            onClick={() => removeAt(i)}
            aria-label="Удалить"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}

      {/* Плейсхолдеры загрузки */}
      {pending.map((p) => (
        <div key={p.tempId} className={`${styles.tile} ${styles.uploading}`}>
          <img src={p.previewUrl} alt="" className={styles.image} />
          <div className={styles.spinner} />
        </div>
      ))}

      {/* Кнопка добавления */}
      {totalCount < max && (
        <button
          type="button"
          className={styles.addBtn}
          onClick={() => inputRef.current?.click()}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="9" cy="9" r="2" />
            <path d="m21 15-5-5L5 21" />
          </svg>
          <span>Добавить</span>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => handleSelect(e.target.files)}
        style={{ display: 'none' }}
      />
    </div>
  );
};